import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Drawer,
  IconButton,
  Link,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ImageIcon from '@mui/icons-material/Image';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';

const API_BASE = 'https://filtrd-prod-f09ee9687166.herokuapp.com/api';
const STORAGE_KEY = 'admin_api_key';

interface XUser {
  id: string;
  username: string;
  name?: string;
  description?: string;
  url?: string;
  profileImageUrl?: string;
  location?: string;
  verified: boolean;
  verifiedType?: string;
  protected: boolean;
  followersCount?: number;
  followingCount?: number;
  postCount?: number;
  likeCount?: number;
  xCreatedAt?: string;
  fetchedAt?: string;
  postsFetchedAt?: string;
  createdAt?: string;
}

interface FiltrdUser {
  id: string;
  name: string;
  email: string;
  xFollows?: XUser[];
}

interface XPostAnnotation {
  id: string;
  domainName: string;
  entityName: string;
  entityDescription?: string;
}

interface XMedia {
  mediaKey: string;
  postId: string;
  type: 'photo' | 'video' | 'animated_gif';
  url?: string;
  previewImageUrl?: string;
  altText?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  variantsJson?: Array<{
    bit_rate?: number;
    content_type: string;
    url: string;
  }>;
}

interface XPost {
  id: string;
  authorId: string;
  text: string;
  fullText?: string;
  conversationId?: string;
  lang?: string;
  possiblySensitive: boolean;
  referencedPostId?: string;
  referencedType?: string;
  retweetCount: number;
  replyCount: number;
  likeCount: number;
  quoteCount: number;
  bookmarkCount: number;
  impressionCount: number;
  xCreatedAt?: string;
  fetchedAt?: string;
  annotations?: XPostAnnotation[];
  media?: XMedia[];
  referencedPost?: XPost;
}

const AdminXUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState<XUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Post drawer state
  const [selectedUser, setSelectedUser] = useState<XUser | null>(null);
  const [posts, setPosts] = useState<XPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fetch new user state
  const [newUsername, setNewUsername] = useState('');
  const [fetchingNewUser, setFetchingNewUser] = useState(false);
  const [refetchingPosts, setRefetchingPosts] = useState(false);

  // Map of xUserId -> list of filtrd user names that follow them
  const [xUserFollowersMap, setXUserFollowersMap] = useState<Record<string, string[]>>({});

  const fetchUsers = useCallback(async () => {
    if (!apiKey) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch X users
      const xUsersResponse = await fetch(`${API_BASE}/x/users`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'x:read',
        },
      });

      if (!xUsersResponse.ok) {
        if (xUsersResponse.status === 401 || xUsersResponse.status === 403) {
          throw new Error('Invalid API key');
        }
        throw new Error(`Failed to fetch: ${xUsersResponse.statusText}`);
      }

      const xUsersData = await xUsersResponse.json();
      const items = xUsersData?.data?.users || xUsersData?.data?.items || xUsersData?.users || xUsersData?.items || xUsersData?.data || [];
      setUsers(Array.isArray(items) ? items : []);

      // Fetch filtrd users with xFollows associations
      const filtrdUsersResponse = await fetch(`${API_BASE}/users?limit=100&include=xFollows`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'users:read',
        },
      });

      if (filtrdUsersResponse.ok) {
        const filtrdUsersData = await filtrdUsersResponse.json();
        const filtrdUsers = filtrdUsersData?.data?.items || [];

        // Build map of xUserId -> list of filtrd user names
        const followersMap: Record<string, string[]> = {};
        filtrdUsers.forEach((user: FiltrdUser) => {
          if (user.xFollows && Array.isArray(user.xFollows)) {
            user.xFollows.forEach((xUser: XUser) => {
              if (!followersMap[xUser.id]) {
                followersMap[xUser.id] = [];
              }
              followersMap[xUser.id].push(user.name);
            });
          }
        });
        setXUserFollowersMap(followersMap);
      }

      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEY, apiKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const fetchUserPosts = useCallback(async (username: string) => {
    if (!apiKey) return;

    setPostsLoading(true);
    setPostsError(null);

    try {
      const response = await fetch(`${API_BASE}/x/users/${username}/posts`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'x:read',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch posts: ${response.statusText}`);
      }

      const data = await response.json();
      const items = data?.data?.posts || data?.data?.items || data?.posts || data?.items || data?.data || [];
      setPosts(Array.isArray(items) ? items : []);
    } catch (err) {
      setPostsError(err instanceof Error ? err.message : 'Failed to fetch posts');
    } finally {
      setPostsLoading(false);
    }
  }, [apiKey]);

  const fetchNewUser = useCallback(async () => {
    if (!apiKey || !newUsername.trim()) return;

    const username = newUsername.trim().replace(/^@/, ''); // Remove @ if present
    setFetchingNewUser(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/x/users/${username}/fetch`, {
        method: 'POST',
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'x:write',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Failed to fetch user: ${response.statusText}`);
      }

      setNewUsername('');
      // Refresh the users list
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user');
    } finally {
      setFetchingNewUser(false);
    }
  }, [apiKey, newUsername, fetchUsers]);

  const refetchUserPosts = useCallback(async (username: string, forceRefresh = false) => {
    if (!apiKey) return;

    setRefetchingPosts(true);
    setPostsError(null);

    try {
      const url = forceRefresh
        ? `${API_BASE}/x/users/${username}/fetch?forceRefresh=true`
        : `${API_BASE}/x/users/${username}/fetch`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'x:write',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Failed to fetch posts: ${response.statusText}`);
      }

      // Refresh the posts list and users list (to update postsFetchedAt)
      await Promise.all([fetchUserPosts(username), fetchUsers()]);

      // Update selected user with new fetch time
      const updatedUsers = await fetch(`${API_BASE}/x/users`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'x:read',
        },
      }).then(res => res.json());

      const updatedUser = (updatedUsers?.data?.users || []).find((u: XUser) => u.username === username);
      if (updatedUser) {
        setSelectedUser(updatedUser);
      }
    } catch (err) {
      setPostsError(err instanceof Error ? err.message : 'Failed to refetch posts');
    } finally {
      setRefetchingPosts(false);
    }
  }, [apiKey, fetchUserPosts, fetchUsers]);

  const handleUserClick = (user: XUser) => {
    setSelectedUser(user);
    setDrawerOpen(true);
    fetchUserPosts(user.username);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedUser(null);
    setPosts([]);
    setPostsError(null);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsers([]);
    localStorage.removeItem(STORAGE_KEY);
    setApiKey('');
  };

  useEffect(() => {
    if (apiKey && !isAuthenticated) {
      fetchUsers();
    }
  }, [apiKey, isAuthenticated, fetchUsers]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.deepNavy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            sx={{
              p: 4,
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: colors.pureWhite,
                mb: 3,
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              X Users Admin
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    color: colors.pureWhite,
                    '& fieldset': {
                      borderColor: colors.whiteAlpha[20],
                    },
                    '&:hover fieldset': {
                      borderColor: colors.whiteAlpha[30],
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.signalOrange,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.slate[400],
                    '&.Mui-focused': {
                      color: colors.signalOrange,
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !apiKey}
                sx={{
                  backgroundColor: colors.signalOrange,
                  '&:hover': {
                    backgroundColor: colors.signalOrangeHover,
                  },
                  '&:disabled': {
                    backgroundColor: colors.slate[600],
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.deepNavy,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/admin')}
              sx={{ color: colors.pureWhite }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{
                color: colors.pureWhite,
                fontWeight: 700,
              }}
            >
              X Users Admin
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="@username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newUsername.trim()) {
                  fetchNewUser();
                }
              }}
              disabled={fetchingNewUser}
              sx={{
                width: 160,
                '& .MuiOutlinedInput-root': {
                  color: colors.pureWhite,
                  '& fieldset': {
                    borderColor: colors.whiteAlpha[30],
                  },
                  '&:hover fieldset': {
                    borderColor: colors.whiteAlpha[50],
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.signalOrange,
                  },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: colors.slate[500],
                  opacity: 1,
                },
              }}
            />
            <Button
              onClick={fetchNewUser}
              disabled={fetchingNewUser || !newUsername.trim()}
              sx={{
                backgroundColor: colors.signalOrange,
                color: colors.pureWhite,
                '&:hover': {
                  backgroundColor: colors.signalOrangeHover,
                },
                '&:disabled': {
                  backgroundColor: colors.slate[600],
                  color: colors.slate[400],
                },
              }}
              variant="contained"
            >
              {fetchingNewUser ? <CircularProgress size={20} color="inherit" /> : 'Fetch User'}
            </Button>
            <Button
              onClick={() => navigate('/admin')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Onboardings
            </Button>
            <Button
              onClick={() => navigate('/admin/users')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Users
            </Button>
            <Button
              onClick={() => navigate('/admin/categories')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Categories
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={loading}
              sx={{
                color: colors.pureWhite,
                borderColor: colors.whiteAlpha[30],
                '&:hover': {
                  borderColor: colors.pureWhite,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: colors.error,
                borderColor: colors.error,
                '&:hover': {
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Logout
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats */}
        <Typography sx={{ mb: 3, color: colors.slate[400] }}>
          Total: {users.length} users
        </Typography>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.signalOrange }} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Username</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Followers</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Following</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Posts</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Users</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Posts Fetched</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Added</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    onClick={() => handleUserClick(user)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: colors.whiteAlpha[10],
                      },
                    }}
                  >
                    <TableCell sx={{ color: colors.signalOrange, fontWeight: 500 }}>
                      @{user.username}
                    </TableCell>
                    <TableCell sx={{ color: colors.pureWhite }}>
                      {user.name || '-'}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {formatNumber(user.followersCount)}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {formatNumber(user.followingCount)}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {formatNumber(user.postCount)}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {xUserFollowersMap[user.id]?.length > 0
                        ? `${xUserFollowersMap[user.id].length} users`
                        : '0'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {user.verified && (
                          <Chip
                            icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                            label={user.verifiedType || 'Verified'}
                            size="small"
                            sx={{
                              backgroundColor: user.verifiedType === 'blue' ? '#1DA1F2' : colors.success,
                              color: colors.pureWhite,
                              height: 20,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                        {user.protected && (
                          <Chip
                            icon={<LockIcon sx={{ fontSize: 14 }} />}
                            label="Private"
                            size="small"
                            sx={{
                              backgroundColor: '#F59E0B',
                              color: colors.deepNavy,
                              height: 20,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                      {formatDate(user.postsFetchedAt)}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                      {formatDate(user.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ color: colors.slate[400], textAlign: 'center', py: 4 }}>
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Posts Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: colors.deepNavy,
            borderLeft: colors.borders.whiteAlpha10,
          },
        }}
      >
        {selectedUser && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header */}
            <Box
              sx={{
                p: 3,
                borderBottom: colors.borders.whiteAlpha10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h5" sx={{ color: colors.pureWhite, fontWeight: 700 }}>
                    {selectedUser.name || selectedUser.username}
                  </Typography>
                  {selectedUser.verified && (
                    <VerifiedIcon sx={{ color: '#1DA1F2', fontSize: 20 }} />
                  )}
                </Box>
                <Link
                  href={`https://x.com/${selectedUser.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: colors.signalOrange, textDecoration: 'none' }}
                >
                  @{selectedUser.username}
                </Link>
                {selectedUser.description && (
                  <Typography sx={{ color: colors.slate[400], mt: 1, fontSize: '0.875rem' }}>
                    {selectedUser.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <Typography sx={{ color: colors.slate[400], fontSize: '0.875rem' }}>
                    <strong style={{ color: colors.pureWhite }}>{formatNumber(selectedUser.followersCount)}</strong> followers
                  </Typography>
                  <Typography sx={{ color: colors.slate[400], fontSize: '0.875rem' }}>
                    <strong style={{ color: colors.pureWhite }}>{formatNumber(selectedUser.followingCount)}</strong> following
                  </Typography>
                </Box>

                {/* Followed by section */}
                {xUserFollowersMap[selectedUser.id] && xUserFollowersMap[selectedUser.id].length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ color: colors.slate[500], fontSize: '0.875rem', mb: 1 }}>
                      Followed by:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {xUserFollowersMap[selectedUser.id].map((userName, idx) => (
                        <Chip
                          key={idx}
                          label={userName}
                          size="small"
                          sx={{
                            backgroundColor: colors.whiteAlpha[10],
                            color: colors.pureWhite,
                            fontSize: '0.75rem',
                            height: 22,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              <IconButton onClick={handleCloseDrawer} sx={{ color: colors.slate[400] }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Posts List */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: colors.pureWhite }}>
                    Posts ({posts.length})
                  </Typography>
                  <Typography sx={{ color: colors.slate[500], fontSize: '0.75rem' }}>
                    Last fetched: {formatRelativeTime(selectedUser.postsFetchedAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => refetchUserPosts(selectedUser.username)}
                    disabled={refetchingPosts || postsLoading}
                    startIcon={refetchingPosts ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                    sx={{
                      color: colors.signalOrange,
                      borderColor: colors.signalOrange,
                      '&:hover': {
                        borderColor: colors.signalOrangeHover,
                        backgroundColor: colors.whiteAlpha[10],
                      },
                      '&:disabled': {
                        color: colors.slate[500],
                        borderColor: colors.slate[600],
                      },
                    }}
                    variant="outlined"
                  >
                    {refetchingPosts ? 'Fetching...' : 'Fetch New'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => refetchUserPosts(selectedUser.username, true)}
                    disabled={refetchingPosts || postsLoading}
                    sx={{
                      color: colors.slate[400],
                      borderColor: colors.slate[600],
                      '&:hover': {
                        borderColor: colors.slate[400],
                        backgroundColor: colors.whiteAlpha[10],
                      },
                      '&:disabled': {
                        color: colors.slate[600],
                        borderColor: colors.slate[600],
                      },
                    }}
                    variant="outlined"
                  >
                    Full Refetch
                  </Button>
                </Box>
              </Box>

              {postsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: colors.signalOrange }} />
                </Box>
              ) : postsError ? (
                <Alert severity="error">{postsError}</Alert>
              ) : posts.length === 0 ? (
                <Typography sx={{ color: colors.slate[400], textAlign: 'center', py: 4 }}>
                  No posts found
                </Typography>
              ) : (
                posts.map((post) => (
                  <Paper
                    key={post.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.whiteAlpha[10],
                      border: colors.borders.whiteAlpha10,
                    }}
                  >
                    {/* Referenced post (for replies and quotes) */}
                    {post.referencedPost && post.referencedType === 'replied_to' && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.5,
                          backgroundColor: colors.whiteAlpha[10],
                          borderLeft: `3px solid ${colors.whiteAlpha[30]}`,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: colors.slate[500], mb: 0.5, display: 'block' }}
                        >
                          Replying to:
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.slate[400],
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                          }}
                        >
                          {post.referencedPost.fullText || post.referencedPost.text}
                        </Typography>
                        {/* Referenced post media */}
                        {post.referencedPost.media && post.referencedPost.media.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {post.referencedPost.media.map((m) => (
                              <Box
                                key={m.mediaKey}
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  backgroundColor: colors.whiteAlpha[10],
                                }}
                              >
                                {m.type === 'photo' && m.url ? (
                                  <img
                                    src={m.url}
                                    alt=""
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : m.previewImageUrl ? (
                                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                    <img
                                      src={m.previewImageUrl}
                                      alt=""
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <PlayCircleFilledIcon
                                      sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        fontSize: 24,
                                        color: colors.pureWhite,
                                        opacity: 0.8,
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <ImageIcon sx={{ color: colors.slate[600] }} />
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Quoted post (similar styling but different label) */}
                    {post.referencedPost && post.referencedType === 'quoted' && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.5,
                          backgroundColor: colors.whiteAlpha[10],
                          border: `1px solid ${colors.whiteAlpha[20]}`,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: colors.slate[500], mb: 0.5, display: 'block' }}
                        >
                          Quoting:
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.slate[400],
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                          }}
                        >
                          {post.referencedPost.fullText || post.referencedPost.text}
                        </Typography>
                      </Box>
                    )}

                    <Typography sx={{ color: colors.pureWhite, mb: 2, whiteSpace: 'pre-wrap' }}>
                      {post.fullText || post.text}
                    </Typography>

                    {/* Media */}
                    {post.media && post.media.length > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {post.media.map((m) => {
                          // Get the best video URL (highest bitrate mp4)
                          const getVideoUrl = (media: XMedia): string | undefined => {
                            if (!media.variantsJson || media.variantsJson.length === 0) {
                              return media.url || media.previewImageUrl;
                            }
                            const mp4Variants = media.variantsJson
                              .filter(v => v.content_type === 'video/mp4')
                              .sort((a, b) => (b.bit_rate || 0) - (a.bit_rate || 0));
                            return mp4Variants[0]?.url || media.variantsJson[0]?.url;
                          };

                          if (m.type === 'photo' && m.url) {
                            return (
                              <Box
                                key={m.mediaKey}
                                component="a"
                                href={m.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'block',
                                  maxWidth: '100%',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: `1px solid ${colors.whiteAlpha[20]}`,
                                }}
                              >
                                <img
                                  src={m.url}
                                  alt={m.altText || 'Post image'}
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: 300,
                                    objectFit: 'contain',
                                    display: 'block',
                                  }}
                                />
                              </Box>
                            );
                          }

                          if ((m.type === 'video' || m.type === 'animated_gif')) {
                            const videoUrl = getVideoUrl(m);
                            const posterUrl = m.previewImageUrl;

                            return (
                              <Box
                                key={m.mediaKey}
                                sx={{
                                  position: 'relative',
                                  maxWidth: '100%',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  border: `1px solid ${colors.whiteAlpha[20]}`,
                                }}
                              >
                                {videoUrl ? (
                                  <Box sx={{ position: 'relative' }}>
                                    <video
                                      src={videoUrl}
                                      poster={posterUrl}
                                      controls
                                      playsInline
                                      preload="metadata"
                                      loop={m.type === 'animated_gif'}
                                      muted={m.type === 'animated_gif'}
                                      autoPlay={m.type === 'animated_gif'}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: 300,
                                        display: 'block',
                                      }}
                                      onError={(e) => {
                                        // If video fails to load, show fallback link
                                        const target = e.currentTarget;
                                        target.style.display = 'none';
                                        const fallback = target.nextElementSibling as HTMLElement;
                                        if (fallback) fallback.style.display = 'flex';
                                      }}
                                    />
                                    <Box
                                      component="a"
                                      href={videoUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        display: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        gap: 1,
                                        p: 2,
                                        backgroundColor: colors.whiteAlpha[10],
                                        borderRadius: 1,
                                        color: colors.slate[400],
                                        textDecoration: 'none',
                                        '&:hover': { backgroundColor: colors.whiteAlpha[20] },
                                      }}
                                    >
                                      <PlayCircleFilledIcon sx={{ fontSize: 32 }} />
                                      <Typography variant="caption">Open video in new tab</Typography>
                                    </Box>
                                  </Box>
                                ) : posterUrl ? (
                                  <Box sx={{ position: 'relative' }}>
                                    <img
                                      src={posterUrl}
                                      alt={m.altText || 'Video preview'}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: 300,
                                        objectFit: 'contain',
                                        display: 'block',
                                      }}
                                    />
                                    <PlayCircleFilledIcon
                                      sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        fontSize: 48,
                                        color: colors.pureWhite,
                                        opacity: 0.8,
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 200,
                                      height: 120,
                                      backgroundColor: colors.whiteAlpha[10],
                                    }}
                                  >
                                    <PlayCircleFilledIcon sx={{ fontSize: 48, color: colors.slate[500] }} />
                                  </Box>
                                )}
                                {m.durationMs && (
                                  <Typography
                                    sx={{
                                      position: 'absolute',
                                      bottom: 8,
                                      right: 8,
                                      backgroundColor: 'rgba(0,0,0,0.7)',
                                      color: colors.pureWhite,
                                      fontSize: '0.7rem',
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 0.5,
                                    }}
                                  >
                                    {Math.floor(m.durationMs / 60000)}:{String(Math.floor((m.durationMs % 60000) / 1000)).padStart(2, '0')}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }

                          // Fallback for unknown media types
                          return (
                            <Chip
                              key={m.mediaKey}
                              icon={<ImageIcon sx={{ fontSize: 14 }} />}
                              label={`${m.type} media`}
                              size="small"
                              sx={{
                                backgroundColor: colors.whiteAlpha[20],
                                color: colors.slate[400],
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {/* Post metadata */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Typography sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                        {formatNumber(post.likeCount)} likes
                      </Typography>
                      <Typography sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                        {formatNumber(post.retweetCount)} retweets
                      </Typography>
                      <Typography sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                        {formatNumber(post.replyCount)} replies
                      </Typography>
                      <Typography sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                        {formatNumber(post.impressionCount)} views
                      </Typography>
                    </Box>

                    {/* Annotations */}
                    {post.annotations && post.annotations.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ color: colors.slate[500], fontSize: '0.7rem', mb: 0.5 }}>
                          Annotations:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {post.annotations.map((ann) => (
                            <Chip
                              key={ann.id}
                              label={`${ann.domainName}: ${ann.entityName}`}
                              size="small"
                              sx={{
                                backgroundColor: colors.whiteAlpha[20],
                                color: colors.slate[400],
                                height: 20,
                                fontSize: '0.65rem',
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Post type and date */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {post.referencedType && (
                          <Chip
                            label={post.referencedType}
                            size="small"
                            sx={{
                              backgroundColor: colors.signalOrange,
                              color: colors.pureWhite,
                              height: 18,
                              fontSize: '0.65rem',
                            }}
                          />
                        )}
                        {post.possiblySensitive && (
                          <Chip
                            label="Sensitive"
                            size="small"
                            sx={{
                              backgroundColor: '#F59E0B',
                              color: colors.deepNavy,
                              height: 18,
                              fontSize: '0.65rem',
                            }}
                          />
                        )}
                      </Box>
                      <Link
                        href={`https://x.com/${selectedUser.username}/status/${post.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: colors.slate[500], fontSize: '0.75rem', textDecoration: 'none' }}
                      >
                        {formatDate(post.xCreatedAt)}
                      </Link>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default AdminXUsersPage;
